import type { Rect, Size } from '@/core/geometry'

export enum FilmFormatId {
  PolaroidClassic = 'polaroid-classic',
  PolaroidGo = 'polaroid-go',
  InstaxMini = 'instax-mini',
}

export interface FilmFormat {
  id: FilmFormatId
  label: string
  filmMm: Size
  windowMm: Rect
  captionAreaMm: Rect
}

export const FILM_FORMATS = [
  {
    id: FilmFormatId.PolaroidClassic,
    label: 'Polaroid Classic',
    filmMm: { width: 88, height: 107 },
    windowMm: { x: 4.5, y: 6, width: 79, height: 79 },
    captionAreaMm: { x: 4.5, y: 88, width: 79, height: 16 },
  },
  {
    id: FilmFormatId.PolaroidGo,
    label: 'Polaroid Go',
    filmMm: { width: 53.9, height: 66.6 },
    windowMm: { x: 3.45, y: 4, width: 47, height: 46 },
    captionAreaMm: { x: 3.45, y: 52.5, width: 47, height: 11 },
  },
  {
    id: FilmFormatId.InstaxMini,
    label: 'Instax Mini',
    filmMm: { width: 54, height: 86 },
    windowMm: { x: 4, y: 6, width: 46, height: 62 },
    captionAreaMm: { x: 4, y: 71, width: 46, height: 11 },
  },
] as const satisfies readonly FilmFormat[]

export const DEFAULT_FILM_FORMAT = FilmFormatId.PolaroidClassic

function assertRectInsideFilm(rect: Rect, film: Size, label: string): void {
  const values = [rect.x, rect.y, rect.width, rect.height]
  if (!values.every(Number.isFinite) || rect.x < 0 || rect.y < 0 || rect.width <= 0 || rect.height <= 0) {
    throw new Error(`${label} must have finite, positive dimensions and a non-negative origin`)
  }
  if (rect.x + rect.width > film.width || rect.y + rect.height > film.height) {
    throw new Error(`${label} must stay inside the film`)
  }
}

function rectsOverlap(first: Rect, second: Rect): boolean {
  return first.x < second.x + second.width
    && first.x + first.width > second.x
    && first.y < second.y + second.height
    && first.y + first.height > second.y
}

export function validateFilmFormats(formats: readonly FilmFormat[] = FILM_FORMATS): void {
  const seenIds = new Set<FilmFormatId>()
  for (const format of formats) {
    if (!Object.values(FilmFormatId).includes(format.id)) {
      throw new Error(`Film format configuration has unknown id "${String(format.id)}"`)
    }
    if (seenIds.has(format.id)) throw new Error(`Film format configuration has duplicate id "${format.id}"`)
    seenIds.add(format.id)
    if (!format.label.trim()) throw new Error(`Film format "${format.id}" requires a label`)
    if (!Number.isFinite(format.filmMm.width) || !Number.isFinite(format.filmMm.height)
      || format.filmMm.width <= 0 || format.filmMm.height <= 0) {
      throw new Error(`Film format "${format.id}" must have finite, positive film dimensions`)
    }
    assertRectInsideFilm(format.windowMm, format.filmMm, `Film format "${format.id}" window`)
    assertRectInsideFilm(format.captionAreaMm, format.filmMm, `Film format "${format.id}" caption area`)
    if (rectsOverlap(format.windowMm, format.captionAreaMm)) {
      throw new Error(`Film format "${format.id}" window and caption area must not overlap`)
    }
  }
  for (const id of Object.values(FilmFormatId)) {
    if (!seenIds.has(id)) throw new Error(`Film format configuration is missing id "${id}"`)
  }
}

export function getFilmFormat(id: FilmFormatId): FilmFormat {
  const format = FILM_FORMATS.find((candidate) => candidate.id === id)
  if (!format) throw new Error(`Unknown film format: "${id}"`)
  return format
}
